import { all, call, put, takeEvery, takeLatest } from "redux-saga/effects";
import * as SavedReportService from "../../services/SavedReportService";
import {
  savedReportCreateFail,
  savedReportCreateRequest,
  savedReportCreateSuccess,
  savedReportDeleteFail,
  savedReportDeleteRequest,
  savedReportDeleteSuccess,
  savedReportDetailsFail,
  savedReportDetailsRequest,
  savedReportDetailsSuccess,
  savedReportHistoryFail,
  savedReportHistoryRequest,
  savedReportHistorySuccess,
  savedReportListFail,
  savedReportListRequest,
  savedReportListSuccess,
  savedReportScheduleDeleteFail,
  savedReportScheduleDeleteRequest,
  savedReportScheduleDeleteSuccess,
  savedReportSchedulePauseFail,
  savedReportSchedulePauseRequest,
  savedReportSchedulePauseSuccess,
  savedReportScheduleResumeFail,
  savedReportScheduleResumeRequest,
  savedReportScheduleResumeSuccess,
  savedReportScheduleSaveFail,
  savedReportScheduleSaveRequest,
  savedReportScheduleSaveSuccess,
  savedReportSendNowFail,
  savedReportSendNowRequest,
  savedReportSendNowSuccess,
  savedReportUpdateFail,
  savedReportUpdateRequest,
  savedReportUpdateSuccess,
} from "../slices/savedReportSlice";

// The API's error body ({ response: "<code>", values: { message }, ... }), or a
// generic one when the request got no answer. A 404 (a saved report deleted,
// for example in another tab) has no error code in its body, so it is given
// one and the admin is told the report is no longer available (B-BUG-5).
const errorOf = (error) => {
  if (error?.response?.status === 404) {
    return { status: 404, response: "SAVED_REPORT_NOT_FOUND" };
  }
  return (
    error?.response?.data || {
      status: 400,
      response: "SOMETHING_ELSE_WRONG",
    }
  );
};

// payload { namePattern }: the list screen's search. The reload after a
// schedule change has none; the list screen filters the rows by name too.
export function* loadSavedReports({ payload } = {}) {
  try {
    const ajax = yield call(
      SavedReportService.getSavedReports,
      payload?.namePattern
    );
    yield put(savedReportListSuccess(ajax.data));
  } catch (error) {
    yield put(savedReportListFail(errorOf(error)));
  }
}

export function* loadSavedReport({ payload }) {
  try {
    const ajax = yield call(SavedReportService.getSavedReport, payload.id);
    yield put(savedReportDetailsSuccess(ajax.data));
  } catch (error) {
    yield put(savedReportDetailsFail(errorOf(error)));
  }
}

export function* createSavedReport({ payload }) {
  try {
    const ajax = yield call(SavedReportService.createSavedReport, payload);
    yield put(savedReportCreateSuccess(ajax.data));
  } catch (error) {
    yield put(savedReportCreateFail(errorOf(error)));
  }
}

// payload { id, ...body, keepStoredName }. keepStoredName (Save on a report
// screen): Save changes the level and filters, not the name, and the name
// the screen has is the one the report had when it was opened. The name is
// left out of the update, and the API keeps the stored name when an update
// sends none, so Save keeps a rename made in another tab or on another
// device since (B-BUG-4). This used to read the stored name first (GET) and
// send it back, which still undid a rename made between the two requests
// (code review phase 2, finding 6).
export function* updateSavedReport({ payload }) {
  const { id, keepStoredName, ...body } = payload;
  if (keepStoredName) {
    delete body.name;
  }
  try {
    const ajax = yield call(SavedReportService.updateSavedReport, id, body);
    yield put(savedReportUpdateSuccess(ajax.data));
  } catch (error) {
    yield put(savedReportUpdateFail({ id, ...errorOf(error) }));
  }
}

export function* deleteSavedReport({ payload }) {
  try {
    yield call(SavedReportService.deleteSavedReport, payload.id);
    yield put(savedReportDeleteSuccess({ id: payload.id }));
  } catch (error) {
    yield put(savedReportDeleteFail(errorOf(error)));
  }
}

// Schedule changes alter the list row (summary, state, next run), so the list
// is loaded again after each one.
function* changeSchedule(serviceCall, args, success, fail) {
  try {
    yield call(serviceCall, ...args);
    yield put(success());
    yield put(savedReportListRequest());
  } catch (error) {
    yield put(fail(errorOf(error)));
  }
}

export function* saveSchedule({ payload }) {
  const { id, ...body } = payload;
  yield* changeSchedule(
    SavedReportService.saveSchedule,
    [id, body],
    savedReportScheduleSaveSuccess,
    savedReportScheduleSaveFail
  );
}

export function* pauseSchedule({ payload }) {
  yield* changeSchedule(
    SavedReportService.pauseSchedule,
    [payload.id],
    savedReportSchedulePauseSuccess,
    savedReportSchedulePauseFail
  );
}

export function* resumeSchedule({ payload }) {
  yield* changeSchedule(
    SavedReportService.resumeSchedule,
    [payload.id],
    savedReportScheduleResumeSuccess,
    savedReportScheduleResumeFail
  );
}

export function* deleteSchedule({ payload }) {
  yield* changeSchedule(
    SavedReportService.deleteSchedule,
    [payload.id],
    savedReportScheduleDeleteSuccess,
    savedReportScheduleDeleteFail
  );
}

export function* sendNow({ payload }) {
  try {
    yield call(SavedReportService.sendNow, payload.id);
    yield put(savedReportSendNowSuccess({ id: payload.id }));
  } catch (error) {
    yield put(savedReportSendNowFail({ id: payload.id, error: errorOf(error) }));
  }
}

export function* loadHistory({ payload }) {
  try {
    const ajax = yield call(SavedReportService.getHistory, payload.id);
    yield put(savedReportHistorySuccess(ajax.data));
  } catch (error) {
    yield put(savedReportHistoryFail(errorOf(error)));
  }
}

export default function* savedReportSaga() {
  yield all([
    takeLatest(savedReportListRequest.type, loadSavedReports),
    takeLatest(savedReportDetailsRequest.type, loadSavedReport),
    takeEvery(savedReportCreateRequest.type, createSavedReport),
    takeEvery(savedReportUpdateRequest.type, updateSavedReport),
    takeEvery(savedReportDeleteRequest.type, deleteSavedReport),
    takeEvery(savedReportScheduleSaveRequest.type, saveSchedule),
    takeEvery(savedReportSchedulePauseRequest.type, pauseSchedule),
    takeEvery(savedReportScheduleResumeRequest.type, resumeSchedule),
    takeEvery(savedReportScheduleDeleteRequest.type, deleteSchedule),
    takeEvery(savedReportSendNowRequest.type, sendNow),
    takeLatest(savedReportHistoryRequest.type, loadHistory),
  ]);
}
